import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AppLayout } from "@/components/layout/AppLayout";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "@/hooks/use-toast";
import {
  MessageSquarePlus, Send, Paperclip, Search, Users, Check, CheckCheck, ArrowLeft, Image as ImageIcon, X,
  MessageCircle, Sparkles, Settings as SettingsIcon, MoreVertical, Trash2, Ban,
  BellOff, Bell, Pin, PinOff, Archive, ArchiveRestore, Eraser, ChevronDown, ChevronRight,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useChitChatProfile } from "@/hooks/useChitChatProfile";
import { useChitChatPresence } from "@/hooks/useChitChatPresence";
import { ChitChatOnboarding } from "@/components/chitchat/ChitChatOnboarding";
import { ChitChatSplash } from "@/components/chitchat/ChitChatSplash";
import { ChitChatOnboardingCarousel } from "@/components/chitchat/ChitChatOnboardingCarousel";
import { StatusPanel } from "@/components/chitchat/StatusPanel";
import { SettingsPanel } from "@/components/chitchat/SettingsPanel";
import { CcAvatar } from "@/components/chitchat/primitives";
import { AfroPattern } from "@/components/chitchat/AfroPattern";
import { BrandMark, BrandWordmark } from "@/components/chitchat/BrandLogo";
import { ChitChatBottomNav } from "@/components/chitchat/ChitChatBottomNav";
import { CommunitiesPanel } from "@/components/chitchat/CommunitiesPanel";
import { ChitChatProfilePanel } from "@/components/chitchat/ChitChatProfilePanel";
import { ChitChatEmptyState } from "@/components/chitchat/ChitChatEmptyState";

type Conversation = {
  id: string;
  is_group: boolean;
  name: string | null;
  avatar_url: string | null;
  last_message_at: string;
  created_by: string;
};

type Member = {
  id: string;
  conversation_id: string;
  user_id: string;
  role: string;
  last_read_at: string;
  muted: boolean;
  pinned: boolean;
  archived: boolean;
  notifications_enabled: boolean;
  cleared_at: string | null;
};

type MyMembershipFlags = {
  last_read_at: string;
  cleared_at: string | null;
  muted: boolean;
  archived: boolean;
  pinned: boolean;
  notifications_enabled: boolean;
};

type Message = {
  id: string;
  conversation_id: string;
  sender_id: string;
  content: string | null;
  attachment_url: string | null;
  attachment_type: string | null;
  attachment_name: string | null;
  created_at: string;
  edited: boolean;
  deleted: boolean;
  deleted_for: string[];
  deleted_for_everyone: boolean;
};

const DELETE_FOR_EVERYONE_WINDOW_MS = 15 * 60 * 1000;

type ProfileLite = { user_id: string; full_name: string | null; avatar_url: string | null };

const initials = (name?: string | null) =>
  (name || "?")
    .split(" ")
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

const formatTime = (iso: string) => {
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  return sameDay
    ? d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleDateString([], { day: "2-digit", month: "short" });
};

export default function ChitChat() {
  const { user } = useAuth();
  const { profile, loading: profileLoading } = useChitChatProfile();
  useChitChatPresence();
  const [tab, setTab] = useState<"chats" | "status" | "communities" | "profile">("chats");
  const [loading, setLoading] = useState(true);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [profilesById, setProfilesById] = useState<Record<string, ProfileLite>>({});
  const [draft, setDraft] = useState("");
  const [search, setSearch] = useState("");
  const [sending, setSending] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [typingUsers, setTypingUsers] = useState<string[]>([]);
  const [unreadByConv, setUnreadByConv] = useState<Record<string, number>>({});
  const [myMembership, setMyMembership] = useState<Record<string, MyMembershipFlags>>({});
  const [showArchived, setShowArchived] = useState(false);
  const [confirmClear, setConfirmClear] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [onboardingDone, setOnboardingDone] = useState(false);
  // First-launch intro flow (splash → carousel). Persisted so returning users skip it.
  const INTRO_KEY = "chitchat:intro-seen:v1";
  const [introStage, setIntroStage] = useState<"splash" | "carousel" | "done">(() => {
    if (typeof window === "undefined") return "done";
    return window.localStorage.getItem(INTRO_KEY) === "1" ? "done" : "splash";
  });
  const [previewIntro, setPreviewIntro] = useState(false);
  const [confirmPreviewIntro, setConfirmPreviewIntro] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<{ id: string; mode: "me" | "everyone" } | null>(null);

  // Block-list awareness
  const [iBlocked, setIBlocked] = useState<Set<string>>(new Set());
  const [peerChitProfile, setPeerChitProfile] = useState<{
    user_id: string;
    avatar_url: string | null;
    last_seen: string | null;
    online: boolean;
    privacy_last_seen: "everyone" | "contacts" | "nobody";
    privacy_photo: "everyone" | "contacts" | "nobody";
  } | null>(null);
  const [peerBlockedMe, setPeerBlockedMe] = useState(false);

  // Load list of users I've blocked
  const loadMyBlocks = async () => {
    if (!user) return;
    const { data } = await supabase
      .from("chitchat_blocks")
      .select("blocked_id")
      .eq("blocker_id", user.id);
    setIBlocked(new Set((data ?? []).map((r: any) => r.blocked_id)));
  };
  useEffect(() => {
    loadMyBlocks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // ---- Deep linking ---------------------------------------------------
  const navigate = useNavigate();
  const { conversationId: urlConvId, messageId: urlMessageId } = useParams<{
    conversationId?: string;
    messageId?: string;
  }>();
  const [highlightedMessageId, setHighlightedMessageId] = useState<string | null>(null);
  const pendingScrollMessageId = useRef<string | null>(null);

  // Sync URL → active conversation. Switch to chats tab whenever a conv is in the URL.
  useEffect(() => {
    if (urlConvId) {
      setTab("chats");
      setActiveId(urlConvId);
      pendingScrollMessageId.current = urlMessageId ?? null;
    }
  }, [urlConvId, urlMessageId]);

  // Helper used by chat list / new-chat dialog to navigate (keeps URL in sync).
  const openConversation = (id: string | null) => {
    if (id) {
      navigate(`/chitchat/c/${id}`);
    } else {
      navigate(`/chitchat`);
      setActiveId(null);
    }
  };

  // Scroll to and briefly highlight a deep-linked message once messages are loaded.
  useEffect(() => {
    const targetId = pendingScrollMessageId.current;
    if (!targetId || !activeId) return;
    if (!messages.some((m) => m.id === targetId)) return;
    const node = document.getElementById(`cc-msg-${targetId}`);
    if (!node) return;
    pendingScrollMessageId.current = null;
    requestAnimationFrame(() => {
      node.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlightedMessageId(targetId);
      window.setTimeout(() => setHighlightedMessageId((cur) => (cur === targetId ? null : cur)), 2200);
    });
  }, [messages, activeId]);

  // Load conversations + own membership
  const loadConversations = async () => {
    if (!user) return;
    const { data: myMemberships } = await supabase
      .from("chitchat_members")
      .select("conversation_id, last_read_at, cleared_at, muted, archived, pinned, notifications_enabled")
      .eq("user_id", user.id);
    const ids = (myMemberships ?? []).map((m: any) => m.conversation_id);
    if (ids.length === 0) {
      setConversations([]);
      setMyMembership({});
      setUnreadByConv({});
      setLoading(false);
      return;
    }
    const { data: convs } = await supabase
      .from("chitchat_conversations")
      .select("*")
      .in("id", ids)
      .order("last_message_at", { ascending: false });

    setConversations((convs as Conversation[]) ?? []);

    // Build flags map keyed by conversation
    const flagsMap: Record<string, MyMembershipFlags> = {};
    (myMemberships ?? []).forEach((m: any) => {
      flagsMap[m.conversation_id] = {
        last_read_at: m.last_read_at,
        cleared_at: m.cleared_at,
        muted: !!m.muted,
        archived: !!m.archived,
        pinned: !!m.pinned,
        notifications_enabled: m.notifications_enabled !== false,
      };
    });
    setMyMembership(flagsMap);

    // Compute unread counts using max(last_read_at, cleared_at) as the floor
    const unread: Record<string, number> = {};
    for (const c of convs ?? []) {
      const f = flagsMap[(c as any).id];
      const floor =
        f?.cleared_at && new Date(f.cleared_at) > new Date(f.last_read_at)
          ? f.cleared_at
          : f?.last_read_at ?? "1970-01-01";
      const { count } = await supabase
        .from("chitchat_messages")
        .select("id", { count: "exact", head: true })
        .eq("conversation_id", (c as any).id)
        .gt("created_at", floor)
        .neq("sender_id", user.id);
      unread[(c as any).id] = count ?? 0;
    }
    setUnreadByConv(unread);

    // Preload member profiles for direct chats (so we can show name)
    const memberRows = await supabase
      .from("chitchat_members")
      .select("conversation_id, user_id")
      .in("conversation_id", ids);
    const userIds = Array.from(new Set((memberRows.data ?? []).map((m: any) => m.user_id)));
    if (userIds.length) {
      const { data: profs } = await supabase
        .from("profiles")
        .select("user_id, full_name, avatar_url")
        .in("user_id", userIds);
      const map: Record<string, ProfileLite> = {};
      (profs ?? []).forEach((p: any) => (map[p.user_id] = p));
      setProfilesById((prev) => ({ ...prev, ...map }));
    }
    setLoading(false);
  };

  useEffect(() => {
    loadConversations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Subscribe to global conversation/message changes for the user
  useEffect(() => {
    if (!user) return;
    const ch = supabase
      .channel("chitchat-global")
      .on("postgres_changes", { event: "*", schema: "public", table: "chitchat_conversations" }, () => {
        loadConversations();
      })
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "chitchat_messages" }, () => {
        loadConversations();
      })
      .subscribe();
    return () => {
      supabase.removeChannel(ch);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Load active conversation messages + members + subscribe
  useEffect(() => {
    if (!activeId || !user) return;
    let cancelled = false;

    const load = async () => {
      const [{ data: msgs }, { data: mems }] = await Promise.all([
        supabase
          .from("chitchat_messages")
          .select("*")
          .eq("conversation_id", activeId)
          .order("created_at", { ascending: true })
          .limit(500),
        supabase.from("chitchat_members").select("*").eq("conversation_id", activeId),
      ]);
      if (cancelled) return;
      setMessages((msgs as Message[]) ?? []);
      setMembers((mems as Member[]) ?? []);

      // Load missing profiles
      const memberUserIds = (mems ?? []).map((m: any) => m.user_id);
      const missing = memberUserIds.filter((id) => !profilesById[id]);
      if (missing.length) {
        const { data: profs } = await supabase
          .from("profiles")
          .select("user_id, full_name, avatar_url")
          .in("user_id", missing);
        const map: Record<string, ProfileLite> = {};
        (profs ?? []).forEach((p: any) => (map[p.user_id] = p));
        setProfilesById((prev) => ({ ...prev, ...map }));
      }

      // Mark as read
      await supabase
        .from("chitchat_members")
        .update({ last_read_at: new Date().toISOString() })
        .eq("conversation_id", activeId)
        .eq("user_id", user.id);
      setUnreadByConv((prev) => ({ ...prev, [activeId]: 0 }));
    };
    load();

    const ch = supabase
      .channel(`chitchat-conv-${activeId}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "chitchat_messages", filter: `conversation_id=eq.${activeId}` },
        (payload) => {
          setMessages((prev) => [...prev, payload.new as Message]);
          // Auto-mark read
          if ((payload.new as Message).sender_id !== user.id) {
            supabase
              .from("chitchat_members")
              .update({ last_read_at: new Date().toISOString() })
              .eq("conversation_id", activeId)
              .eq("user_id", user.id);
          }
        },
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "chitchat_messages", filter: `conversation_id=eq.${activeId}` },
        (payload) => {
          const updated = payload.new as Message;
          setMessages((prev) => prev.map((m) => (m.id === updated.id ? { ...m, ...updated } : m)));
        },
      )
      .on(
        "postgres_changes",
        { event: "DELETE", schema: "public", table: "chitchat_messages", filter: `conversation_id=eq.${activeId}` },
        (payload) => {
          const removedId = (payload.old as any)?.id;
          if (removedId) setMessages((prev) => prev.filter((m) => m.id !== removedId));
        },
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "chitchat_typing", filter: `conversation_id=eq.${activeId}` },
        async () => {
          const { data } = await supabase
            .from("chitchat_typing")
            .select("user_id, updated_at")
            .eq("conversation_id", activeId);
          const cutoff = Date.now() - 5000;
          const others = (data ?? [])
            .filter((t: any) => t.user_id !== user.id && new Date(t.updated_at).getTime() > cutoff)
            .map((t: any) => t.user_id);
          setTypingUsers(others);
        },
      )
      .subscribe();

    return () => {
      cancelled = true;
      supabase.removeChannel(ch);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId, user?.id]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length, activeId]);

  const activeConv = useMemo(
    () => conversations.find((c) => c.id === activeId) ?? null,
    [conversations, activeId],
  );

  // Identify the other user in a direct chat (null for groups / unloaded members)
  const directPeerId = useMemo(() => {
    if (!activeConv || activeConv.is_group) return null;
    const other = members.find((m) => m.conversation_id === activeConv.id && m.user_id !== user?.id);
    return other?.user_id ?? null;
  }, [activeConv, members, user?.id]);

  const iBlockedPeer = !!directPeerId && iBlocked.has(directPeerId);

  // Fetch peer's ChitChat profile (privacy + last seen). If RLS hides the row
  // even though a base profile exists, we infer that the peer has blocked us.
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!directPeerId) {
        setPeerChitProfile(null);
        setPeerBlockedMe(false);
        return;
      }
      const { data: cc } = await supabase
        .from("chitchat_profiles")
        .select("user_id, avatar_url, last_seen, online, privacy_last_seen, privacy_photo")
        .eq("user_id", directPeerId)
        .maybeSingle();
      if (cancelled) return;
      if (cc) {
        setPeerChitProfile(cc as any);
        setPeerBlockedMe(false);
        return;
      }
      // Fallback: row hidden by RLS likely means peer blocked us
      const { data: baseProf } = await supabase
        .from("profiles")
        .select("user_id")
        .eq("user_id", directPeerId)
        .maybeSingle();
      if (cancelled) return;
      setPeerChitProfile(null);
      setPeerBlockedMe(!!baseProf);
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [directPeerId]);

  const composerLocked = iBlockedPeer || peerBlockedMe;

  const conversationLabel = (c: Conversation): { title: string; avatar?: string | null } => {
    if (c.is_group) return { title: c.name || "Group", avatar: c.avatar_url };
    // Direct: find the other member
    const memberRows = members.filter((m) => m.conversation_id === c.id);
    const other = memberRows.find((m) => m.user_id !== user?.id);
    const prof = other ? profilesById[other.user_id] : undefined;
    return { title: prof?.full_name || "Direct chat", avatar: prof?.avatar_url };
  };

  // For sidebar (we may not have members loaded for every conv); fall back to name
  const sidebarLabel = (c: Conversation) => {
    if (c.is_group) return { title: c.name || "Group", avatar: c.avatar_url };
    // attempt to find a profile via the active members cache
    return { title: c.name || "Direct chat", avatar: c.avatar_url };
  };

  // ---- Per-conversation membership actions --------------------------------
  const updateMembership = async (convId: string, patch: Partial<MyMembershipFlags>) => {
    if (!user) return;
    setMyMembership((prev) => ({
      ...prev,
      [convId]: {
        ...(prev[convId] ?? {
          last_read_at: new Date(0).toISOString(),
          cleared_at: null,
          muted: false,
          archived: false,
          pinned: false,
          notifications_enabled: true,
        }),
        ...patch,
      },
    }));
    await supabase
      .from("chitchat_members")
      .update(patch)
      .eq("conversation_id", convId)
      .eq("user_id", user.id);
  };

  const togglePin = (convId: string) => {
    const f = myMembership[convId];
    void updateMembership(convId, { pinned: !f?.pinned });
  };
  const toggleMute = (convId: string) => {
    const f = myMembership[convId];
    void updateMembership(convId, { muted: !f?.muted });
  };
  const toggleArchive = (convId: string) => {
    const f = myMembership[convId];
    void updateMembership(convId, { archived: !f?.archived, ...(f?.archived ? {} : { pinned: false }) });
    if (!f?.archived && activeId === convId) openConversation(null);
  };
  const clearConversation = async (convId: string) => {
    if (!user) return;
    const now = new Date().toISOString();
    await updateMembership(convId, { cleared_at: now, last_read_at: now });
    if (activeId === convId) setMessages([]);
    setUnreadByConv((prev) => ({ ...prev, [convId]: 0 }));
    setConfirmClear(null);
  };

  // ---- Block / unblock peer ------------------------------------------------
  const blockPeer = async (peerId: string) => {
    if (!user) return;
    const { error } = await supabase
      .from("chitchat_blocks")
      .insert({ blocker_id: user.id, blocked_id: peerId });
    if (error && !error.message.toLowerCase().includes("duplicate")) {
      toast({ title: "Could not block", description: error.message, variant: "destructive" });
      return;
    }
    setIBlocked((prev) => new Set(prev).add(peerId));
    toast({ title: "Contact blocked", description: "They can no longer message you here." });
  };
  const unblockPeer = async (peerId: string) => {
    if (!user) return;
    const { error } = await supabase
      .from("chitchat_blocks")
      .delete()
      .eq("blocker_id", user.id)
      .eq("blocked_id", peerId);
    if (error) {
      toast({ title: "Could not unblock", description: error.message, variant: "destructive" });
      return;
    }
    setIBlocked((prev) => {
      const next = new Set(prev);
      next.delete(peerId);
      return next;
    });
    toast({ title: "Contact unblocked" });
  };
  const matchesSearch = (c: Conversation) => {
    if (!search.trim()) return true;
    return sidebarLabel(c).title.toLowerCase().includes(search.toLowerCase());
  };

  // Group conversations into Pinned / Active / Archived sections.
  const { pinnedConvs, activeConvs, archivedConvs, archivedUnreadTotal } = useMemo(() => {
    const pinned: Conversation[] = [];
    const active: Conversation[] = [];
    const archived: Conversation[] = [];
    let archivedUnread = 0;
    for (const c of conversations) {
      const f = myMembership[c.id];
      if (!matchesSearch(c)) continue;
      if (f?.archived) {
        archived.push(c);
        // Only un-muted archived chats contribute to the section badge total.
        if (!f.muted) archivedUnread += unreadByConv[c.id] ?? 0;
      } else if (f?.pinned) {
        pinned.push(c);
      } else {
        active.push(c);
      }
    }
    return {
      pinnedConvs: pinned,
      activeConvs: active,
      archivedConvs: archived,
      archivedUnreadTotal: archivedUnread,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversations, myMembership, unreadByConv, search]);
  const sendMessage = async (attachment?: { url: string; type: string; name: string }) => {
    if (!activeId || !user) return;
    const content = draft.trim();
    if (!content && !attachment) return;
    setSending(true);
    const { error } = await supabase.from("chitchat_messages").insert({
      conversation_id: activeId,
      sender_id: user.id,
      content: content || null,
      attachment_url: attachment?.url ?? null,
      attachment_type: attachment?.type ?? null,
      attachment_name: attachment?.name ?? null,
    });
    setSending(false);
    if (error) {
      toast({ title: "Failed to send", description: error.message, variant: "destructive" });
      return;
    }
    setDraft("");
  };

  const handleTyping = async () => {
    if (!activeId || !user) return;
    await supabase
      .from("chitchat_typing")
      .upsert({ conversation_id: activeId, user_id: user.id, updated_at: new Date().toISOString() });
  };

  const handleFile = async (file: File) => {
    if (!activeId || !user) return;
    const isImage = file.type.startsWith("image/");
    const path = `${activeId}/${user.id}-${Date.now()}-${file.name}`;
    const { error } = await supabase.storage.from("chitchat-attachments").upload(path, file, {
      cacheControl: "3600",
      upsert: false,
    });
    if (error) {
      toast({ title: "Upload failed", description: error.message, variant: "destructive" });
      return;
    }
    const { data: signed } = await supabase.storage
      .from("chitchat-attachments")
      .createSignedUrl(path, 60 * 60 * 24 * 7);
    if (signed?.signedUrl) {
      await sendMessage({ url: signed.signedUrl, type: isImage ? "image" : "file", name: file.name });
    }
  };

  // ---- Delete message ------------------------------------------------
  const canDeleteForEveryone = (m: Message) => {
    if (!user || m.sender_id !== user.id) return false;
    if (m.deleted_for_everyone) return false;
    return Date.now() - new Date(m.created_at).getTime() < DELETE_FOR_EVERYONE_WINDOW_MS;
  };

  const isHiddenForMe = (m: Message) =>
    !!user && Array.isArray(m.deleted_for) && m.deleted_for.includes(user.id);

  const deleteForMe = async (messageId: string) => {
    if (!user) return;
    const target = messages.find((m) => m.id === messageId);
    if (!target) return;
    const nextDeletedFor = Array.from(new Set([...(target.deleted_for ?? []), user.id]));
    setMessages((prev) =>
      prev.map((m) => (m.id === messageId ? { ...m, deleted_for: nextDeletedFor } : m)),
    );
    const { error } = await supabase
      .from("chitchat_messages")
      .update({ deleted_for: nextDeletedFor })
      .eq("id", messageId);
    if (error) {
      setMessages((prev) =>
        prev.map((m) => (m.id === messageId ? { ...m, deleted_for: target.deleted_for } : m)),
      );
      toast({
        title: "Couldn't delete",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const deleteForEveryone = async (messageId: string) => {
    if (!user) return;
    const target = messages.find((m) => m.id === messageId);
    if (!target) return;
    if (!canDeleteForEveryone(target)) {
      toast({
        title: "Too late",
        description: "You can only delete-for-everyone within 15 minutes of sending.",
        variant: "destructive",
      });
      return;
    }
    setMessages((prev) =>
      prev.map((m) =>
        m.id === messageId
          ? { ...m, deleted_for_everyone: true, content: null, attachment_url: null, attachment_type: null, attachment_name: null }
          : m,
      ),
    );
    const { error } = await supabase
      .from("chitchat_messages")
      .update({
        deleted_for_everyone: true,
        content: null,
        attachment_url: null,
        attachment_type: null,
        attachment_name: null,
      })
      .eq("id", messageId);
    if (error) {
      const { data: fresh } = await supabase
        .from("chitchat_messages")
        .select("*")
        .eq("id", messageId)
        .maybeSingle();
      if (fresh) {
        setMessages((prev) => prev.map((m) => (m.id === messageId ? (fresh as Message) : m)));
      }
      toast({ title: "Couldn't delete for everyone", description: error.message, variant: "destructive" });
    }
  };

  // First-launch intro: splash → carousel. Shown to brand-new users
  // (no existing chitchat profile) AND only once per device. Can also be
  // triggered manually via the "Preview intro" button below.
  const isFirstTimeUser = !profileLoading && !profile && !onboardingDone;
  const showIntro = isFirstTimeUser || previewIntro;

  // Persist carousel slide position so users resume mid-carousel
  const SLIDE_KEY = "chitchat:intro-slide:v1";
  const [initialSlide, setInitialSlide] = useState<number>(() => {
    if (typeof window === "undefined") return 0;
    const saved = window.localStorage.getItem(SLIDE_KEY);
    return saved ? Math.max(0, Math.min(2, parseInt(saved, 10))) : 0;
  });

  if (showIntro && introStage === "splash") {
    return <ChitChatSplash onDone={() => setIntroStage("carousel")} />;
  }
  if (showIntro && introStage === "carousel") {
    return (
      <ChitChatOnboardingCarousel
        initialSlide={initialSlide}
        onSlideChange={(slideIndex) => {
          try {
            window.localStorage.setItem(SLIDE_KEY, String(slideIndex));
          } catch { /* ignore */ }
        }}
        onDone={() => {
          try {
            window.localStorage.setItem(INTRO_KEY, "1");
            window.localStorage.removeItem(SLIDE_KEY);
          } catch { /* ignore */ }
          setIntroStage("done");
          setPreviewIntro(false);
        }}
      />
    );
  }

  // Onboarding gate
  if (!profileLoading && !profile && !onboardingDone) {
    return (
      <AppLayout>
        <ChitChatOnboarding onDone={() => setOnboardingDone(true)} />
      </AppLayout>
    );
  }

  // Reusable wrappers for the chat list sections.
  const ConvSection = ({
    title,
    icon,
    children,
  }: {
    title: string;
    icon?: React.ReactNode;
    children: React.ReactNode;
  }) => (
    <div>
      <div className="flex items-center gap-2 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground bg-muted/30">
        {icon}
        {title}
      </div>
      <ul className="divide-y">{children}</ul>
    </div>
  );

  const renderConvRow = (c: Conversation) => {
    const lbl = sidebarLabel(c);
    const unread = unreadByConv[c.id] ?? 0;
    const f = myMembership[c.id];
    const muted = !!f?.muted;
    const pinned = !!f?.pinned;
    const archived = !!f?.archived;
    return (
      <li
        key={c.id}
        className={cn(
          "p-3 cursor-pointer hover:bg-muted/50 flex items-center gap-3",
          activeId === c.id && "bg-muted",
          archived && "opacity-80",
        )}
        onClick={() => openConversation(c.id)}
      >
        <Avatar className="h-10 w-10 shrink-0">
          {lbl.avatar ? <AvatarImage src={lbl.avatar} /> : null}
          <AvatarFallback>
            {c.is_group ? <Users className="h-4 w-4" /> : initials(lbl.title)}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5 min-w-0">
              <p className={cn("font-medium truncate text-sm", unread > 0 && !muted && "font-semibold")}>
                {lbl.title}
              </p>
              {pinned && <Pin className="h-3 w-3 text-muted-foreground shrink-0" />}
              {muted && <BellOff className="h-3 w-3 text-muted-foreground shrink-0" />}
            </div>
            <span className="text-[10px] text-muted-foreground shrink-0">
              {formatTime(c.last_message_at)}
            </span>
          </div>
          <p className="text-xs text-muted-foreground truncate">
            {c.is_group ? "Group chat" : "Direct message"}
          </p>
        </div>
        {unread > 0 && (
          <Badge
            variant={muted ? "secondary" : "default"}
            className={cn(
              "ml-1 h-5 min-w-5 px-1.5 rounded-full text-[10px]",
              muted && "bg-muted text-muted-foreground hover:bg-muted",
            )}
            aria-label={muted ? `${unread} unread (muted)` : `${unread} unread`}
          >
            {unread > 99 ? "99+" : unread}
          </Badge>
        )}
      </li>
    );
  };

  return (
    <AppLayout>
      <div className="chitchat-theme h-[calc(100vh-9rem)] md:h-[calc(100vh-7rem)] flex flex-col rounded-2xl overflow-hidden border border-cc-border bg-cc-bg">
        <Tabs value={tab} onValueChange={(v) => setTab(v as any)} className="flex-1 flex flex-col">
          {/* Top brand bar */}
          <div className="relative flex items-center justify-between gap-2 px-3 py-2.5 bg-cc-surface border-b border-cc-border overflow-hidden">
            {/* Subtle triangle pattern in top-right corner @ 5% */}
            <div className="absolute top-0 right-0 w-32 h-full pointer-events-none">
              <AfroPattern variant="triangles" opacity={0.05} color="hsl(var(--cc-terracotta))" />
            </div>
            <div className="relative flex items-center gap-2">
              <BrandMark size={32} />
              <BrandWordmark className="text-lg" />
            </div>
            <div className="relative flex items-center gap-1">
              <Button
                size="sm"
                variant="ghost"
                className="h-8 px-2 text-[10px] text-cc-ink-soft hover:text-cc-terracotta"
                onClick={() => setConfirmPreviewIntro(true)}
                title="Preview the splash + onboarding carousel"
              >
                Preview intro
              </Button>
            </div>
          </div>

          <TabsContent value="chats" className="flex-1 overflow-hidden m-0 p-3 bg-cc-bg">
            <div className="flex items-center justify-end mb-2">
              <NewChatDialog
                open={showNew}
                onOpenChange={setShowNew}
                blockedUserIds={iBlocked}
                onCreated={(id) => { loadConversations(); openConversation(id); }}
              />
            </div>

        <div className="flex-1 grid grid-cols-1 md:grid-cols-[320px_1fr] gap-3 overflow-hidden h-[calc(100%-3rem)]">
          {/* Sidebar */}
          <div className={cn("border rounded-lg bg-card flex flex-col overflow-hidden", activeId && "hidden md:flex")}>
            <div className="p-2 border-b">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search chats"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-8 h-9"
                />
              </div>
            </div>
            <ScrollArea className="flex-1">
              {loading ? (
                <div className="p-3 space-y-2">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton key={i} className="h-14 w-full" />
                  ))}
                </div>
              ) : pinnedConvs.length + activeConvs.length + archivedConvs.length === 0 ? (
                <div className="p-6 text-center text-sm text-muted-foreground">
                  No chats yet. Start a new conversation.
                </div>
              ) : (
                <div>
                  {pinnedConvs.length > 0 && (
                    <ConvSection title="Pinned" icon={<Pin className="h-3 w-3" />}>
                      {pinnedConvs.map((c) => renderConvRow(c))}
                    </ConvSection>
                  )}
                  {activeConvs.length > 0 && (
                    <ul className="divide-y">{activeConvs.map((c) => renderConvRow(c))}</ul>
                  )}
                  {archivedConvs.length > 0 && (
                    <div className="border-t mt-1">
                      <button
                        type="button"
                        onClick={() => setShowArchived((v) => !v)}
                        className="w-full flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground hover:bg-muted/50"
                      >
                        {showArchived ? (
                          <ChevronDown className="h-3.5 w-3.5" />
                        ) : (
                          <ChevronRight className="h-3.5 w-3.5" />
                        )}
                        <Archive className="h-3.5 w-3.5" />
                        <span className="font-medium">Archived</span>
                        <span className="ml-1">({archivedConvs.length})</span>
                        {archivedUnreadTotal > 0 && (
                          <Badge variant="secondary" className="ml-auto h-5 min-w-5 px-1.5 rounded-full text-[10px]">
                            {archivedUnreadTotal}
                          </Badge>
                        )}
                      </button>
                      {showArchived && (
                        <ul className="divide-y bg-muted/20">{archivedConvs.map((c) => renderConvRow(c))}</ul>
                      )}
                    </div>
                  )}
                </div>
              )}
            </ScrollArea>
          </div>

          {/* Chat panel */}
          <div className={cn("border rounded-lg bg-card flex flex-col overflow-hidden", !activeId && "hidden md:flex")}>
            {!activeConv ? (
              <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
                Select a chat to start messaging
              </div>
            ) : (
              <>
                <div className="p-3 border-b flex items-center gap-3">
                  <Button size="icon" variant="ghost" className="md:hidden" onClick={() => openConversation(null)}>
                    <ArrowLeft className="h-4 w-4" />
                  </Button>
                  {(() => {
                    const lbl = conversationLabel(activeConv);
                    // Apply peer photo privacy for direct chats
                    const photoMode = peerChitProfile?.privacy_photo ?? "everyone";
                    const showPhoto =
                      activeConv.is_group ||
                      (!iBlockedPeer && !peerBlockedMe && photoMode !== "nobody");
                    const avatarSrc = showPhoto ? lbl.avatar : null;

                    // Subtitle priorities: blocked banner copy lives below, here
                    // we show typing → group count → last seen (if allowed) → "Direct message".
                    let subtitle: string;
                    if (typingUsers.length > 0) subtitle = "typing...";
                    else if (activeConv.is_group) subtitle = `${members.length} members`;
                    else if (iBlockedPeer) subtitle = "Blocked";
                    else if (peerBlockedMe) subtitle = "Unavailable";
                    else {
                      const lsMode = peerChitProfile?.privacy_last_seen ?? "everyone";
                      const canSeeLast = lsMode !== "nobody";
                      if (canSeeLast && peerChitProfile?.online) subtitle = "online";
                      else if (canSeeLast && peerChitProfile?.last_seen)
                        subtitle = `last seen ${formatTime(peerChitProfile.last_seen)}`;
                      else subtitle = "Direct message";
                    }
                    return (
                      <>
                        <Avatar className="h-9 w-9">
                          {avatarSrc ? <AvatarImage src={avatarSrc} /> : null}
                          <AvatarFallback>
                            {activeConv.is_group ? <Users className="h-4 w-4" /> : initials(lbl.title)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{lbl.title}</p>
                          <p className="text-[11px] text-muted-foreground truncate">{subtitle}</p>
                        </div>
                        {activeId && (() => {
                          const f = myMembership[activeId];
                          return (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button size="icon" variant="ghost" aria-label="Chat options">
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-52">
                                <DropdownMenuItem onClick={() => togglePin(activeId)}>
                                  {f?.pinned ? <PinOff className="h-4 w-4 mr-2" /> : <Pin className="h-4 w-4 mr-2" />}
                                  {f?.pinned ? "Unpin chat" : "Pin chat"}
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => toggleMute(activeId)}>
                                  {f?.muted ? <Bell className="h-4 w-4 mr-2" /> : <BellOff className="h-4 w-4 mr-2" />}
                                  {f?.muted ? "Unmute" : "Mute notifications"}
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => toggleArchive(activeId)}>
                                  {f?.archived ? <ArchiveRestore className="h-4 w-4 mr-2" /> : <Archive className="h-4 w-4 mr-2" />}
                                  {f?.archived ? "Unarchive" : "Archive chat"}
                                </DropdownMenuItem>
                                {directPeerId && (
                                  <>
                                    <DropdownMenuSeparator />
                                    {iBlockedPeer ? (
                                      <DropdownMenuItem onClick={() => unblockPeer(directPeerId)}>
                                        <Ban className="h-4 w-4 mr-2" />
                                        Unblock contact
                                      </DropdownMenuItem>
                                    ) : (
                                      <DropdownMenuItem
                                        onClick={() => blockPeer(directPeerId)}
                                        className="text-destructive focus:text-destructive"
                                      >
                                        <Ban className="h-4 w-4 mr-2" />
                                        Block contact
                                      </DropdownMenuItem>
                                    )}
                                  </>
                                )}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={() => setConfirmClear(activeId)}
                                  className="text-destructive focus:text-destructive"
                                >
                                  <Eraser className="h-4 w-4 mr-2" />
                                  Clear chat
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          );
                        })()}
                      </>
                    );
                  })()}
                </div>

                {/* Blocked-state guidance banner */}
                {composerLocked && (
                  <div className="px-3 py-2 border-b bg-muted/40 flex items-start gap-2 text-xs">
                    <Ban className="h-4 w-4 mt-0.5 shrink-0 text-destructive" aria-hidden />
                    <div className="flex-1">
                      {iBlockedPeer ? (
                        <>
                          <p className="font-medium text-foreground">You blocked this contact.</p>
                          <p className="text-muted-foreground">
                            Unblock them to send messages, see their photo, status, or last seen.
                          </p>
                        </>
                      ) : (
                        <>
                          <p className="font-medium text-foreground">You can't message this contact right now.</p>
                          <p className="text-muted-foreground">
                            Their privacy settings prevent new messages. You'll be notified if this changes.
                          </p>
                        </>
                      )}
                    </div>
                    {iBlockedPeer && directPeerId && (
                      <Button size="sm" variant="outline" className="h-7" onClick={() => unblockPeer(directPeerId)}>
                        Unblock
                      </Button>
                    )}
                  </div>
                )}

                <div ref={scrollRef} className="relative flex-1 p-4 bg-cc-bg overflow-y-auto">
                  {/* Adinkra concentric watermark @ 2.5% */}
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="relative w-[320px] h-[320px]">
                      <AfroPattern variant="adinkra" opacity={0.025} color="hsl(var(--cc-terracotta))" />
                    </div>
                  </div>
                  <div className="space-y-2 max-w-3xl mx-auto">
                    {messages
                      .filter((m) => {
                        if (isHiddenForMe(m)) return false;
                        const cleared = activeId ? myMembership[activeId]?.cleared_at : null;
                        if (cleared && new Date(m.created_at) <= new Date(cleared)) return false;
                        return true;
                      })
                      .map((m, idx, visible) => {
                      const mine = m.sender_id === user?.id;
                      const prof = profilesById[m.sender_id];
                      const showHeader =
                        idx === 0 || visible[idx - 1].sender_id !== m.sender_id;
                      const tombstone = m.deleted_for_everyone;
                      return (
                        <div
                          key={m.id}
                          id={`cc-msg-${m.id}`}
                          className={cn(
                            "group flex gap-2 scroll-mt-24 transition-colors rounded-xl px-1 py-0.5",
                            mine ? "justify-end" : "justify-start",
                            highlightedMessageId === m.id && "bg-cc-gold/25 ring-2 ring-cc-burnt/40",
                          )}
                        >
                          {!mine && (
                            <div className="w-7 shrink-0">
                              {showHeader && (
                                <Avatar className="h-7 w-7">
                                  {prof?.avatar_url ? <AvatarImage src={prof.avatar_url} /> : null}
                                  <AvatarFallback className="text-[10px]">
                                    {initials(prof?.full_name)}
                                  </AvatarFallback>
                                </Avatar>
                              )}
                            </div>
                          )}
                          <div className={cn("flex items-center gap-1 max-w-[80%]", mine ? "flex-row" : "flex-row-reverse")}>
                            {!tombstone && (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    size="icon"
                                    variant="ghost"
                                    className="h-7 w-7 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity shrink-0"
                                    aria-label="Message actions"
                                  >
                                    <MoreVertical className="h-3.5 w-3.5" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align={mine ? "end" : "start"} className="w-56">
                                  <DropdownMenuItem onClick={() => setConfirmDelete({ id: m.id, mode: "me" })}>
                                    <Trash2 className="h-4 w-4 mr-2" /> Delete for me
                                  </DropdownMenuItem>
                                  {canDeleteForEveryone(m) && (
                                    <>
                                      <DropdownMenuSeparator />
                                      <DropdownMenuItem
                                        onClick={() => setConfirmDelete({ id: m.id, mode: "everyone" })}
                                        className="text-destructive focus:text-destructive"
                                      >
                                        <Ban className="h-4 w-4 mr-2" /> Delete for everyone
                                      </DropdownMenuItem>
                                    </>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                            <div
                              className={cn(
                                "px-3 py-2 text-sm break-words",
                                tombstone
                                  ? "bg-muted text-muted-foreground italic border border-dashed rounded-2xl"
                                  : mine
                                  ? "chitchat-bubble-me"
                                  : "chitchat-bubble-them",
                              )}
                            >
                              {tombstone ? (
                                <p className="flex items-center gap-1.5">
                                  <Ban className="h-3.5 w-3.5" />
                                  <span>{mine ? "You deleted this message" : "This message was deleted"}</span>
                                </p>
                              ) : (
                                <>
                                  {!mine && showHeader && activeConv.is_group && (
                                    <p className="text-[10px] font-semibold mb-0.5 opacity-80">
                                      {prof?.full_name || "Member"}
                                    </p>
                                  )}
                                  {m.attachment_url && m.attachment_type === "image" && (
                                    <img
                                      src={m.attachment_url}
                                      alt={m.attachment_name || "image"}
                                      className="rounded-md mb-1 max-h-72 object-cover"
                                    />
                                  )}
                                  {m.attachment_url && m.attachment_type !== "image" && (
                                    <a
                                      href={m.attachment_url}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="flex items-center gap-2 underline mb-1"
                                    >
                                      <Paperclip className="h-3.5 w-3.5" />
                                      <span className="truncate">{m.attachment_name || "Attachment"}</span>
                                    </a>
                                  )}
                                  {m.content && <p className="whitespace-pre-wrap">{m.content}</p>}
                                </>
                              )}
                              <div
                                className={cn(
                                  "flex items-center gap-1 mt-1 text-[10px]",
                                  tombstone
                                    ? "text-muted-foreground/70 justify-start"
                                    : mine
                                    ? "text-primary-foreground/70 justify-end"
                                    : "text-muted-foreground",
                                )}
                              >
                                <span>{formatTime(m.created_at)}</span>
                                {mine && !tombstone && <CheckCheck className="h-3 w-3" />}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="border-t p-2 flex items-end gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) handleFile(f);
                      e.target.value = "";
                    }}
                  />
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={composerLocked}
                  >
                    <Paperclip className="h-4 w-4" />
                  </Button>
                  <Input
                    placeholder={
                      iBlockedPeer
                        ? "Unblock this contact to send a message"
                        : peerBlockedMe
                        ? "You can't message this contact"
                        : "Type a message"
                    }
                    value={draft}
                    onChange={(e) => {
                      setDraft(e.target.value);
                      handleTyping();
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        if (!composerLocked) sendMessage();
                      }
                    }}
                    disabled={composerLocked}
                    className="flex-1"
                  />
                  <Button
                    size="icon"
                    onClick={() => sendMessage()}
                    disabled={sending || composerLocked || !draft.trim()}
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
          </TabsContent>

          <TabsContent value="status" className="flex-1 overflow-auto m-0">
            <StatusPanel />
          </TabsContent>

          <TabsContent value="communities" className="flex-1 overflow-auto m-0">
            <CommunitiesPanel
              onOpen={(id) => { setTab("chats"); openConversation(id); }}
              onCreate={() => { setTab("chats"); setShowNew(true); }}
            />
          </TabsContent>

          <TabsContent value="profile" className="flex-1 overflow-auto m-0">
            <ChitChatProfilePanel
              onEditProfile={() => { /* opens settings panel below */ }}
              onOpenPrivacy={() => { /* anchor to privacy section */ }}
            />
            {/* Full editing form lives here for parity with the previous Settings tab */}
            <div className="border-t border-cc-border mt-2">
              <SettingsPanel />
            </div>
          </TabsContent>

          {/* Bottom navigation */}
          <ChitChatBottomNav value={tab} onChange={(v) => setTab(v)} />
        </Tabs>
      </div>

      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={(o) => { if (!o) setConfirmDelete(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmDelete?.mode === "everyone" ? "Delete for everyone?" : "Delete for me?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete?.mode === "everyone"
                ? "This message will be removed for all participants. This cannot be undone."
                : "This message will be hidden from your view. Other participants will still see it."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                const c = confirmDelete;
                setConfirmDelete(null);
                if (!c) return;
                if (c.mode === "everyone") await deleteForEveryone(c.id);
                else await deleteForMe(c.id);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={!!confirmClear}
        onOpenChange={(o) => { if (!o) setConfirmClear(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Clear this chat?</AlertDialogTitle>
            <AlertDialogDescription>
              All messages will be hidden from your view and the unread count will reset to zero.
              Other participants will still see the conversation history.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { if (confirmClear) void clearConversation(confirmClear); }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Clear chat
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={confirmPreviewIntro}
        onOpenChange={(o) => { if (!o) setConfirmPreviewIntro(false); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Replay the intro?</AlertDialogTitle>
            <AlertDialogDescription>
              This will clear your onboarding flag and show the splash screen and carousel again. You can skip anytime.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                window.localStorage.removeItem(INTRO_KEY);
                setIntroStage("splash");
                setPreviewIntro(true);
                setConfirmPreviewIntro(false);
              }}
            >
              Replay intro
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}

// =============== New Chat Dialog ===============
function NewChatDialog({
  open,
  onOpenChange,
  onCreated,
  blockedUserIds,
}: {
  open: boolean;
  onOpenChange: (b: boolean) => void;
  onCreated: (conversationId: string) => void;
  blockedUserIds: Set<string>;
}) {
  const { user } = useAuth();
  const [tab, setTab] = useState<"direct" | "group">("direct");
  const [people, setPeople] = useState<ProfileLite[]>([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<string[]>([]);
  const [groupName, setGroupName] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!open || !user) return;
    (async () => {
      const { data } = await supabase
        .from("profiles")
        .select("user_id, full_name, avatar_url")
        .neq("user_id", user.id)
        .order("full_name", { ascending: true })
        .limit(200);
      setPeople((data as ProfileLite[]) ?? []);
    })();
  }, [open, user?.id]);

  // Hide users I've blocked from the picker entirely. Users who blocked me
  // are already filtered out by RLS on `chitchat_profiles` reads downstream.
  const filtered = people.filter(
    (p) =>
      !blockedUserIds.has(p.user_id) &&
      (p.full_name || "").toLowerCase().includes(search.toLowerCase()),
  );

  const reset = () => {
    setSelected([]);
    setGroupName("");
    setSearch("");
    setTab("direct");
  };

  const startDirect = async (otherUserId: string) => {
    if (!user) return;
    if (blockedUserIds.has(otherUserId)) {
      toast({
        title: "Contact is blocked",
        description: "Unblock them from the chat menu before starting a new conversation.",
        variant: "destructive",
      });
      return;
    }
    setCreating(true);
    // Find existing direct chat with this user
    const { data: myMems } = await supabase
      .from("chitchat_members")
      .select("conversation_id")
      .eq("user_id", user.id);
    const myConvIds = (myMems ?? []).map((m: any) => m.conversation_id);
    if (myConvIds.length) {
      const { data: theirs } = await supabase
        .from("chitchat_members")
        .select("conversation_id")
        .eq("user_id", otherUserId)
        .in("conversation_id", myConvIds);
      const sharedIds = (theirs ?? []).map((m: any) => m.conversation_id);
      if (sharedIds.length) {
        const { data: directConvs } = await supabase
          .from("chitchat_conversations")
          .select("id")
          .in("id", sharedIds)
          .eq("is_group", false)
          .limit(1);
        if (directConvs && directConvs.length > 0) {
          setCreating(false);
          onOpenChange(false);
          reset();
          onCreated((directConvs[0] as any).id);
          return;
        }
      }
    }
    // Create new direct
    const { data: conv, error } = await supabase
      .from("chitchat_conversations")
      .insert({ is_group: false, created_by: user.id })
      .select()
      .single();
    if (error || !conv) {
      setCreating(false);
      toast({ title: "Failed to start chat", description: error?.message, variant: "destructive" });
      return;
    }
    const { error: memErr } = await supabase.from("chitchat_members").insert([
      { conversation_id: conv.id, user_id: user.id, role: "admin" },
      { conversation_id: conv.id, user_id: otherUserId, role: "member" },
    ]);
    setCreating(false);
    if (memErr) {
      toast({ title: "Failed to add members", description: memErr.message, variant: "destructive" });
      return;
    }
    onOpenChange(false);
    reset();
    onCreated(conv.id);
  };

  const createGroup = async () => {
    if (!user || !groupName.trim() || selected.length === 0) return;
    setCreating(true);
    const { data: conv, error } = await supabase
      .from("chitchat_conversations")
      .insert({ is_group: true, name: groupName.trim(), created_by: user.id })
      .select()
      .single();
    if (error || !conv) {
      setCreating(false);
      toast({ title: "Failed to create group", description: error?.message, variant: "destructive" });
      return;
    }
    const rows = [
      { conversation_id: conv.id, user_id: user.id, role: "admin" },
      ...selected.map((uid) => ({ conversation_id: conv.id, user_id: uid, role: "member" })),
    ];
    const { error: memErr } = await supabase.from("chitchat_members").insert(rows);
    setCreating(false);
    if (memErr) {
      toast({ title: "Failed to add members", description: memErr.message, variant: "destructive" });
      return;
    }
    onOpenChange(false);
    reset();
    onCreated(conv.id);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button size="sm" className="gap-2">
          <MessageSquarePlus className="h-4 w-4" /> New chat
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Start a conversation</DialogTitle>
        </DialogHeader>
        <Tabs value={tab} onValueChange={(v) => setTab(v as any)}>
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="direct">Direct</TabsTrigger>
            <TabsTrigger value="group">Group</TabsTrigger>
          </TabsList>
          <TabsContent value="direct" className="space-y-2">
            <Input placeholder="Search people" value={search} onChange={(e) => setSearch(e.target.value)} />
            <ScrollArea className="h-72 border rounded-md">
              <ul className="divide-y">
                {filtered.map((p) => (
                  <li
                    key={p.user_id}
                    className="p-2 flex items-center gap-2 hover:bg-muted cursor-pointer"
                    onClick={() => !creating && startDirect(p.user_id)}
                  >
                    <Avatar className="h-8 w-8">
                      {p.avatar_url ? <AvatarImage src={p.avatar_url} /> : null}
                      <AvatarFallback>{initials(p.full_name)}</AvatarFallback>
                    </Avatar>
                    <span className="text-sm truncate">{p.full_name || "Unnamed"}</span>
                  </li>
                ))}
                {filtered.length === 0 && (
                  <li className="p-6 text-center text-xs text-muted-foreground">No users found</li>
                )}
              </ul>
            </ScrollArea>
          </TabsContent>
          <TabsContent value="group" className="space-y-2">
            <Input placeholder="Group name" value={groupName} onChange={(e) => setGroupName(e.target.value)} />
            <Input placeholder="Search people" value={search} onChange={(e) => setSearch(e.target.value)} />
            <ScrollArea className="h-56 border rounded-md">
              <ul className="divide-y">
                {filtered.map((p) => {
                  const checked = selected.includes(p.user_id);
                  return (
                    <li
                      key={p.user_id}
                      className="p-2 flex items-center gap-2 hover:bg-muted cursor-pointer"
                      onClick={() =>
                        setSelected((prev) =>
                          prev.includes(p.user_id) ? prev.filter((x) => x !== p.user_id) : [...prev, p.user_id],
                        )
                      }
                    >
                      <Checkbox checked={checked} />
                      <Avatar className="h-7 w-7">
                        {p.avatar_url ? <AvatarImage src={p.avatar_url} /> : null}
                        <AvatarFallback>{initials(p.full_name)}</AvatarFallback>
                      </Avatar>
                      <span className="text-sm truncate">{p.full_name || "Unnamed"}</span>
                    </li>
                  );
                })}
              </ul>
            </ScrollArea>
            <p className="text-[11px] text-muted-foreground">{selected.length} selected</p>
            <DialogFooter>
              <Button onClick={createGroup} disabled={creating || !groupName.trim() || selected.length === 0}>
                Create group
              </Button>
            </DialogFooter>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
